// pages/StepPembayaran.tsx

import { Card } from "@/components/ui/card";
import ProgressIndicator from "@/components/private/ProgressIndicator";
import { useUser } from "@/context/useUser";
import { useActivePeriod } from "@/hooks/useActivePeriod";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Lock } from "lucide-react";

// Import Views
import { RegularPaymentView } from "@/components/private/order/RegularPaymentView";
import { PicPaymentView } from "@/components/private/order/PicPaymentView";
import type { UserData } from "@/types/pic";

const SkeletonContainer = () => (
    <div className="mx-auto py-12 space-y-10 pt-0 max-w-6xl animate-pulse">
        <Card className="border-0 shadow-lg bg-white dark:bg-gray-800 mb-3 h-32" />
        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
    </div>
);

export default function StepPembayaran() {
    const queryClient = useQueryClient();
    const { data: rawUser, isLoading: loadingUser } = useUser();
    const user = rawUser as UserData | null;
    const { data: activePeriod, isLoading: loadingPeriod } = useActivePeriod();

    // 1. Cek status payment (Sumber Kebenaran Tunggal)
    const { data: paymentData, isLoading: loadingPay } = useQuery({
        queryKey: ["payment-check", user?.participant_id, activePeriod?.id],
        queryFn: async () => {
            const { data } = await axiosClient.get(
                `/participants/${user?.participant_id}/registrations/period/${activePeriod?.id}/payment`
            );
            return data.data;
        },
        enabled: !!user?.participant_id && !!activePeriod?.id,
    });

    const hasInvoice = !!paymentData && (paymentData.status === "unpaid" || paymentData.status === "paid");

    const updateStep = async (step: number) => {
        if (!user?.participant_id || !activePeriod?.id) return;
        try {
            await axiosClient.put(
                `/participants/${user.participant_id}/registrations/step/${activePeriod.id}`,
                { current_step: step }
            );

            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["registration-status"] }),
                queryClient.invalidateQueries({ queryKey: ["registration-step"] }),
                queryClient.invalidateQueries({ queryKey: ["payment-check"] })
            ]);

        } catch (error) {
            console.error("Gagal update step:", error);
            toast.error("Gagal memperbarui progres.");
            throw error;
        }
    };

    const handleNext = async () => {
        await updateStep(4);
    };

    const handleBack = async () => {
        if (hasInvoice) {
            toast.error("Tagihan sudah aktif. Tidak dapat kembali ke pemilihan paket.");
            return;
        }
        await updateStep(2);
    };

    if (loadingUser || loadingPeriod || loadingPay) return <SkeletonContainer />;
    if (!activePeriod) return <div className="text-center py-20 text-gray-500">Tidak ada periode aktif.</div>;
    if (!user) return null;

    return (
        <div className="mx-auto py-12 space-y-8 pt-0 max-w-6xl">
            {/* Header dengan Ornamen Bali */}
            <Card className="relative border-0 shadow-lg bg-white dark:bg-gray-800 mb-6 overflow-hidden rounded-2xl transition-all duration-300">
                <img
                    src="/ornamen/ukiran-corner.png"
                    alt="Ukiran Bali"
                    className="absolute top-0 left-0 w-40 md:w-48 opacity-40 pointer-events-none z-0"
                />
                <img
                    src="/ornamen/ukiran-corner.png"
                    alt="Ukiran Bali"
                    className="absolute top-0 right-0 w-40 md:w-48 scale-x-[-1] opacity-40 pointer-events-none z-0"
                />
                <div
                    className="absolute inset-0 opacity-[0.1] dark:opacity-[0.05] pointer-events-none"
                    style={{
                        backgroundImage: `radial-gradient(currentColor 0.8px, transparent 0.8px)`,
                        backgroundSize: '12px 12px',
                    }}
                />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-32 w-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative z-10 px-10 md:px-16 py-8 flex flex-col items-center text-center space-y-3">
                    <h2 className="text-xl font-black tracking-tight text-slate-900 dark:text-white flex items-center justify-center gap-2 uppercase">
                        Pembayaran {user.role_id === 3 ? "Kolektif" : "Registrasi"}
                    </h2>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 leading-relaxed italic max-w-md mx-auto">
                        {user.role_id === 3
                            ? "Selesaikan pembayaran kolektif untuk seluruh anggota tim Anda."
                            : "Pilih salah satu metode pembayaran yang tersedia di bawah ini."}
                    </p>
                </div>
            </Card>

            {/* Progress Step */}
            <ProgressIndicator currentStep={3} />

            {/* Alert Tagihan Aktif (Melebar di bawah step) */}
            {hasInvoice && (
                <div className="animate-in slide-in-from-top-2 duration-500">
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-center justify-between shadow-sm">
                        <div className="flex items-center gap-3">
                            <div className="bg-amber-100 dark:bg-amber-800 p-2 rounded-lg">
                                <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-amber-800 dark:text-amber-300 uppercase tracking-tight">
                                    Tagihan Aktif Ditemukan
                                </p>
                                <p className="text-[10px] font-medium text-amber-600/80 dark:text-amber-400/80 italic">
                                    Proses pemilihan paket dikunci untuk menjaga integritas data pembayaran.
                                </p>
                            </div>
                        </div>
                        <AlertCircle className="w-5 h-5 text-amber-400 opacity-50 hidden md:block" />
                    </div>
                </div>
            )}

            {/* View Selector */}
            <div className="mt-4">
                {user.role_id === 3 ? (
                    <PicPaymentView
                        user={user}
                        activePeriodId={activePeriod.id}
                        onNext={handleNext}
                        backDisabled={hasInvoice}
                        onBack={handleBack}
                    />
                ) : (
                    <RegularPaymentView
                        user={user}
                        activePeriodId={activePeriod.id}
                        onNext={handleNext}
                        backDisabled={hasInvoice}
                        onBack={handleBack}
                    />
                )}
            </div>
        </div>
    );
}